import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

export interface SignaturePadHandle {
  /**
   * Returns a PNG `data:` URL of the signature.
   * - For drawn signatures, returns the trimmed bounding-box image.
   * - For typed signatures, renders the typed name into a hidden canvas with a script-like font.
   * Returns `null` when the user has not provided any signature.
   */
  getSignatureBase64: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface SignaturePadProps {
  /** Used as default text in "type instead" mode (typically the acceptor's name). */
  defaultTypedName?: string;
}

const TYPED_CANVAS_WIDTH = 600;
const TYPED_CANVAS_HEIGHT = 160;

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  ({ defaultTypedName }, ref) => {
    const [mode, setMode] = useState<'draw' | 'type'>('draw');
    const [typedName, setTypedName] = useState<string>(defaultTypedName ?? '');
    const sigCanvasRef = useRef<SignatureCanvas | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [drawWidth, setDrawWidth] = useState<number>(600);

    // Resize the canvas to its container width so it's responsive.
    useEffect(() => {
      const update = () => {
        if (containerRef.current) {
          setDrawWidth(Math.max(280, Math.floor(containerRef.current.clientWidth)));
        }
      };
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }, []);

    const renderTypedToCanvas = (name: string): string | null => {
      if (name.trim() === '') {
        return null;
      }
      const canvas = document.createElement('canvas');
      canvas.width = TYPED_CANVAS_WIDTH;
      canvas.height = TYPED_CANVAS_HEIGHT;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return null;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#0f172a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '64px "Caveat", "Brush Script MT", cursive';
      ctx.fillText(name, canvas.width / 2, canvas.height / 2);
      return canvas.toDataURL('image/png');
    };

    useImperativeHandle(
      ref,
      () => ({
        getSignatureBase64: () => {
          if (mode === 'type') {
            return renderTypedToCanvas(typedName);
          }
          const sig = sigCanvasRef.current;
          if (!sig || sig.isEmpty()) {
            return null;
          }
          // `getTrimmedCanvas` produces a tight crop around the strokes.
          try {
            return sig.getTrimmedCanvas().toDataURL('image/png');
          } catch {
            return sig.toDataURL('image/png');
          }
        },
        clear: () => {
          if (mode === 'draw') {
            sigCanvasRef.current?.clear();
          } else {
            setTypedName('');
          }
        },
        isEmpty: () => {
          if (mode === 'type') {
            return typedName.trim() === '';
          }
          const sig = sigCanvasRef.current;
          return !sig || sig.isEmpty();
        },
      }),
      [mode, typedName]
    );

    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-slate-600">Signature</span>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setMode('draw')}
              className={[
                'rounded px-2 py-1',
                mode === 'draw' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              Draw
            </button>
            <button
              type="button"
              onClick={() => setMode('type')}
              className={[
                'rounded px-2 py-1',
                mode === 'type' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              Type instead
            </button>
          </div>
        </div>

        {mode === 'draw' ? (
          <div ref={containerRef} className="overflow-hidden rounded-md border border-dashed border-slate-300 bg-white">
            <SignatureCanvas
              ref={(instance) => {
                sigCanvasRef.current = instance;
              }}
              penColor="#0f172a"
              backgroundColor="#ffffff"
              canvasProps={{
                width: drawWidth,
                height: 160,
                className: 'block w-full touch-none',
              }}
            />
          </div>
        ) : (
          <div ref={containerRef} className="rounded-md border border-dashed border-slate-300 bg-white p-3">
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full name"
              className="w-full border-0 bg-transparent p-0 text-center font-script text-4xl text-slate-900 outline-none focus:ring-0"
            />
          </div>
        )}

        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (mode === 'draw') {
                sigCanvasRef.current?.clear();
              } else {
                setTypedName('');
              }
            }}
            className="text-xs text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
          >
            Clear
          </button>
        </div>
      </div>
    );
  }
);

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
